import type { IntlShape } from 'react-intl';

/**
 * A short month ends with a full stop in many locales ("nov." in Portuguese, Spanish and French,
 * "Nov." in German). A message that puts such a date at the end of a sentence adds its own full
 * stop, so the reader saw "até 4 de nov.." (QA 2026-10-28, lit-13).
 *
 * The fix starts at the date format: the app intl remembers each formatted date that ends with a
 * full stop. When a message gets that date as a value and its text puts a full stop right after
 * it, the message keeps one full stop. Only the date and the full stop after it change; text of a
 * user (a song title "Wait..") and an ellipsis stay as they are.
 */

const MAX_REMEMBERED_DATES = 500;

type FormatMessage = IntlShape['formatMessage'];
type MessageValues = Parameters<FormatMessage>[1];

/** Escapes a text for use in a regular expression. */
function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The formatted dates (of this intl) that end with a full stop. */
export class DatesWithFullStop {
  private readonly dates = new Set<string>();

  remember(formatted: string): string {
    if (formatted.endsWith('.') && !this.dates.has(formatted)) {
      if (this.dates.size >= MAX_REMEMBERED_DATES) {
        const oldest = this.dates.values().next();
        if (oldest.done !== true) {
          this.dates.delete(oldest.value);
        }
      }
      this.dates.add(formatted);
    }
    return formatted;
  }

  /** The string values of a message that are dates with a full stop. */
  datesIn(values: MessageValues): string[] {
    if (values === undefined || values === null) {
      return [];
    }
    const found: string[] = [];
    for (const value of Object.values(values)) {
      if (typeof value === 'string' && this.dates.has(value)) {
        found.push(value);
      }
    }
    return found;
  }
}

/** Removes the full stop that a message puts right after a date that ends with one. */
export function dropFullStopAfterDates(text: string, dates: readonly string[]): string {
  let result = text;
  for (const date of dates) {
    if (result.includes(`${date}.`)) {
      result = result.replace(new RegExp(`${escapeRegExp(date)}\\.(?!\\.)`, 'g'), date);
    }
  }
  return result;
}

function fixResult<T>(result: T, dates: readonly string[]): T {
  if (dates.length === 0) {
    return result;
  }
  if (typeof result === 'string') {
    return dropFullStopAfterDates(result, dates) as T;
  }
  if (Array.isArray(result)) {
    return result.map((part: unknown) => (typeof part === 'string' ? dropFullStopAfterDates(part, dates) : part)) as T;
  }
  return result;
}

/**
 * Makes the date formatters of `intl` remember dates that end with a full stop, and makes
 * `formatMessage` (also `$t` and every <FormattedMessage>, which calls it) keep one full stop
 * after such a date.
 */
export function keepOneFullStopAfterDates<T extends IntlShape>(intl: T): T {
  const dates = new DatesWithFullStop();

  const { formatDate, formatDateTimeRange, formatMessage } = intl;
  intl.formatDate = ((...args: Parameters<typeof formatDate>) =>
    dates.remember(formatDate(...args))) as typeof formatDate;
  intl.formatDateTimeRange = ((...args: Parameters<typeof formatDateTimeRange>) =>
    dates.remember(formatDateTimeRange(...args))) as typeof formatDateTimeRange;

  const withOneFullStop = ((...args: Parameters<FormatMessage>) =>
    fixResult(formatMessage(...args), dates.datesIn(args[1]))) as FormatMessage;
  intl.formatMessage = withOneFullStop;
  intl.$t = withOneFullStop;
  return intl;
}
